<template>
  <v-container fluid class="fill-height justify-center">
    <v-row class="auth-wrapper">
      <v-col cols="12" sm="12" md="12" lg="6" class="pa-0 auth-image-content">
        <div class="img-content">
          <img v-if="isMobileDevice" src="../../assets/images/login-img-sm.png" alt="Login_Img">
          <img v-else src="../../assets/images/login-img.png" alt="Login_Img">
        </div>
      </v-col>
      <v-col cols="12" sm="12" md="12" lg="6" class="pa-0 auth-form-content">
        <v-card class="pa-10 flat px-3" flat>
          <div v-if="state.type === 'temp-password'" class="text-center mb-3 mx-auto temp-password-logo">
            <img
              src="../../assets/images/yester-envelope.svg"
              alt="Yester Temp password image"
            />
          </div>
          <div v-else class="text-center mb-3 form-logo">
            <img
              src="img/icons/Yester_logo_RGB_COLOUR_POSITIVE.png"
              alt="Yester"
              width="30%"
              style="vertical-align: middle;"
              class="mr-1"
            >
          </div>
          <div class="text-center">
            <v-btn
              v-if="state.ssoRedirect"
              style="min-width: 88px;"
              color="error"
              text
              ripple
              :loading="loading"
              @click="cancelRedirectTimer"
            >
              {{ $t(state.cancelButtonText) }}
            </v-btn>
          </div>
          <v-row class="form-row ma-0">
            <v-col cols="12" sm="12" md="12" offset="1" offset-sm="0"
                   offset-md="0"
            >
              <v-card-text>
                <v-form
                  v-show="!state.submitFormDisabled && !state.ssoRedirect"
                  ref="form"
                  v-model="valid"
                  class="form-content"
                  lazy-validation
                  @submit.prevent="onSubmit"
                >
                  <p v-if="loginService.mode === 'forceNewPassword'" class="login-hint">
                    {{ $t('Login with your temporary password was successful.') }} <br> {{ $t('Please enter your new password twice to continue.') }}
                  </p>
                  <v-text-field
                    ref="input"
                    v-model="username"
                    class="mb-3 settings-field settings-field--login"
                    :label="$t('Username')"
                    placeholder=" "
                    persistent-placeholder
                    name="login"
                    type="text"
                    hide-details="auto"
                    outlined
                    :disabled="!state.usernameEnabled"
                    :rules="usernameRules"
                    validate-on-blur
                    @input="clearErrors"
                  />

                  <v-expand-transition>
                    <!-- password field for login and password reset -->
                    <v-text-field
                      v-if="state.passwordEnabled"
                      v-model="password"
                      :append-icon="showPassword ? mdiEye : mdiEyeOff"
                      :type="showPassword ? 'text' : 'password'"
                      :rules="passwordRules"
                      class="mb-2 settings-field settings-field--login"
                      :hide-details="hideDetails() ? true : 'auto'"
                      outlined
                      :label="state.passwordLabel ? $t(state.passwordLabel) : $t('Password')"
                      placeholder=" "
                      persistent-placeholder
                      @click:append="showPassword = !showPassword"
                      @input="clearErrors"
                    />
                  </v-expand-transition>
                  <!-- password confirmation field for password reset -->
                  <v-text-field
                    v-if="state.confirmPasswordEnabled"
                    v-model="confirmPassword"
                    :append-icon="showConfirmPassword ? mdiEye : mdiEyeOff"
                    :type="showConfirmPassword ? 'text' : 'password'"
                    :rules="confirmPasswordRules"
                    class="mb-2 settings-field settings-field--login"
                    :hide-details="hideDetails() && hideResetPasswordValidationError ? 'auto' : true"
                    outlined
                    :label="state.confirmPasswordLabel ? $t(state.confirmPasswordLabel) : $t('Confirm password')"
                    @click:append="showConfirmPassword = !showConfirmPassword"
                    @input="clearErrors"
                  />
                  <p v-if="hideDetails() && !hideResetPasswordValidationError && !valid && submitted" class="password-validation-error">
                    {{ $t('The password must be at least 8 characters long and contain a number, symbol, uppercase and lowercase letter.') }}
                  </p>
                  <v-text-field
                    v-if="state.codeEnabled"
                    v-model="code"
                    :append-icon="showPassword ? mdiEye : mdiEyeOff"
                    :type="showPassword ? 'text' : 'password'"
                    class="mb-2 settings-field settings-field--login"
                    hide-details="auto"
                    outlined
                    :label="$t('Insert Code sent to your e-mail')"
                    @click:append="showPassword = !showPassword"
                    @input="clearErrors"
                  />
                  <v-row>
                    <v-col cols="8" sm="12" md="12" offset="1" offset-sm="0"
                           offset-md="0" class="pb-0 form-link"
                    >
                      <div class="text-h6 text-center font-weight-regular">
                        {{ state.errorText }}
                      </div>
                      <a v-if="state.forgotPasswordEnabled" class="d-inline-block text-body-2 text-decoration-none mb-0 ml-3" @click="startRecovery">Forgot your password?</a>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="8" sm="12" md="12" offset="1" offset-sm="0"
                           offset-md="0" class="py-0 mb-3 form-link"
                    >
                      <a v-if="!(state.success || 1) || state.backToLoginEnabled" class="d-inline-block text-body-2 text-decoration-none mb-2 ml-3" @click="onBackToLoginClick">
                        {{ $t('Back to login') }}
                      </a>
                    </v-col>
                  </v-row>
                  <!-- submit button -->

                  <div class="d-flex flex-row-reverse mt-0">
                    <v-btn
                      class="py-6 px-5"
                      style="min-width: 88px;"
                      color="primary"
                      dark
                      :class="{'btn-disabled': !username || !password}"
                      type="submit"
                      ripple
                      :loading="loading"
                    >
                      {{ state.submitButtonText }}
                    </v-btn>
                  </div>
                </v-form>
                <div v-show="state.submitFormDisabled">
                  <div class="text-h6 text-center font-weight-regular" :class="{'temp-password-message mx-auto': state.type === 'temp-password'}">
                    {{ state.message }}
                  </div>
                  <div class="text-h6 text-center font-weight-regular">
                    <v-btn
                      color="primary"
                      class="mb-0 mt-10 py-7 px-5"
                      @click="onBackToLoginClick"
                    >
                      {{ $t('Back to login') }}
                    </v-btn>
                  </div>
                  <p v-if="state.type === 'temp-password'" class="text-center mt-12 send-again-content">
                    <span class="mr-1">Didn't recieve the email?</span>
                    <v-btn
                      class="px-0 py-0"
                      color="primary"
                      plain
                      small
                      :ripple="false"
                      @click="handleResendCode"
                    >
                      Send Again
                    </v-btn>
                  </p>
                </div>
              </v-card-text>
            </v-col>
          </v-row>
        </v-card>
        <!-- recovery/reset success message -->
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import {
  mdiLock, mdiAccountBox, mdiEye, mdiEyeOff,
} from '@mdi/js';

import Vue from 'vue';
import { password } from '@/common-utils/validation';

const passwordValidator = password;

const vectorIcons = {
  mdiLock,
  mdiAccountBox,
  mdiEyeOff,
  mdiEye,
};
export default {
  name: 'LoginComponent',
  inject: ['loginService'],
  data() {
    return {
      ...vectorIcons,
      state: this.loginService.form,
      valid: true,
      username: '',
      password: '',
      code: '',
      confirmPassword: '',
      loading: false,
      submitted: false,
      usernameError: '',
      passwordError: '',
      recoveryError: '',
      resetError: '',
      showPassword: false,
      showConfirmPassword: false,
      resolve_message: '',
      inputBackground: 'rgba(255, 255, 255, 0.12)',
      user: {},
      autofilled: false,
    };
  },
  computed: {
    usernameRules() {
      return [
        (v) => !!v || this.$t('Username is required'),
        // (v) => validateEmail(v) || 'Invalid email address',
        !this.state.recoveryError || this.state.recoveryError,
        !this.state.usernameError || this.state.usernameError,
      ];
    },
    passwordRules() {
      if (!this.submitted && this.loginService.mode === 'forceNewPassword') {
        return [];
      }

      const validationRules = [
        (v) => !!v || this.$t('Password is required'),
        !this.state.passwordError || this.state.passwordError,
      ];

      if (this.hideDetails()) {
        const validPasswordRule = (v) => (v.trim().length >= 8 && passwordValidator(v))
        || '';
        validationRules.splice(1, 0, validPasswordRule);
      }
      return validationRules;
    },
    confirmPasswordRules() {
      if (!this.submitted && this.loginService.mode === 'forceNewPassword') {
        return [];
      }

      const validationRules = [
        (v) => !!v || this.$t('Confirm password is required'),
        (v) => v === this.password || 'Passwords must match',
        !this.state.resetError || this.state.resetError,
      ];

      if (this.hideDetails()) {
        const validPasswordRule = (v) => (v.trim().length >= 8 && passwordValidator(v))
        || '';
        validationRules.splice(1, 0, validPasswordRule);
      }

      return validationRules;
    },
    hideResetPasswordValidationError() {
      const hideError = this.password !== ''
      && this.confirmPassword !== ''
      && this.password.trim().length >= 8 && passwordValidator(this.password)
        && this.confirmPassword.trim().length >= 8 && passwordValidator(this.confirmPassword);

      return hideError;
    },
  },
  watch: {
    state() {
      if (!this.state.keepPassword) {
        this.password = '';
        this.confirmPassword = '';
      }
    },
    password() {
      this.autofilled = true;
    },
  },
  created() {
    if (this.$route && this.$route.query.code && this.$route.query.username) {
      this.loginService.setMode('resetRequired');
      this.username = this.$route.query.username;
      this.code = this.$route.query.code;
    }
  },
  methods: {
    hideDetails() {
      const hideDetailsModes = ['resetRequired', 'forceNewPassword'];
      return hideDetailsModes.includes(this.loginService.mode);
    },
    handleResendCode() {
      const state = this.loginService.setMode('recover');
      Vue.set(this, 'state', state);
    },
    clearErrors() {
      this.$refs.form.resetValidation();
      this.state.recoveryError = '';
      this.state.usernameError = '';
      this.state.passwordError = '';
      this.state.resetError = '';
    },
    onBackToLoginClick() {
      const state = this.loginService.setMode('attemptSSO');
      Vue.set(this, 'state', state);
    },
    startRecovery() {
      const state = this.loginService.startRecovery();
      Vue.set(this, 'state', state);
    },
    cancelRedirectTimer() {
      console.log('cancelRedirectTimer');
      const state = this.loginService.cancelRedirectTimer();
      Vue.set(this, 'state', state);
    },
    async onSubmit() {
      this.submitted = true;
      await this.$nextTick();
      await this.$refs.form.validate();
      if (!this.valid) {
        return;
      }
      this.loading = true;
      const state = await this.loginService.onSubmit(this.username, this.password, this.code);
      Vue.set(this, 'state', state);
      this.loading = false;
      this.submitted = false;
    },
  },
};
</script>
<style lang="scss">
.yester-logo {
  background: url('@/assets/logo.svg') no-repeat;
  height: 80px;
  width: 300px;
  justify-self: flex-start;
  align-self: flex-start;
}

.text-body-2 {
  font-weight: 400 !important;
  font-size: 13px !important;
  line-height: 24px !important;
}
.img-content {
  width: 100%;
  height: 100%;
  & img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.auth-wrapper {
  width: 100% !important;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

.auth-form-content {
  display: flex;
  justify-content: center;
  align-items: center;
  .form-logo {
    img {
      width: 164px !important;
      height: 49px !important;
    }
  }
  .form-row {
    width: 100% !important;
  }
  .v-card--flat {
    width: 100%;
  }
  .form-content {
    width: 497px;
    margin-left: auto !important;
    margin-right: auto !important;
  }
}
.auth-image-content {
  height: 100%;
  width: 100%;
  background-image: linear-gradient(to bottom left, #2D30A1 , #487D97);
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  order: 2;
  .img-content {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    img {
      width: 629px;
      height: 687px;
      object-fit: contain;
    }
  }
  &::after {
    content: "";
    position: absolute;
    right: 0;
    bottom: 80px;
    width: 0px;
    width: 0px;
    border-top: 60px solid transparent;
    border-bottom: 60px solid transparent;
    border-right: 60px solid #58FF81;
  }
}
.temp-password-logo {
  img {
    width: 100px;
    height: 100px;
    object-fit: cover;
  }
}
.temp-password-message {
  font-weight: 400;
  &.text-h6 {
    font-size: 16px !important;
  }
  line-height: 1.7 !important;
  width: 35%;
}

.send-again-content {
  display: flex;
  justify-content: center;
  align-items: center;
  span {
    font-size: 14px;
    color: $yester-gray;
  }

  button {
    text-transform: none;
    letter-spacing: 0;
    font-size: 14px;
    .v-btn__content {
      opacity: 1 !important;
      color: $yester-lila-light;
      font-weight: 400;
    }
  }
}
.password-validation-error {
  color: #f67777;
  font-size: 12px;
  line-height: 1.4;
}

.login-hint {
  text-align: center;
  color: #777;
}

//RSPONSIVENESS
@media (max-width: 710px), (max-height: 480px) and (orientation: landscape)  {
  .v-card {
    padding-left: 0px !important;
    padding-right: 0px !important;
  }

  .auth-wrapper {
    width: 100% !important;
    display: block !important;
    overflow: auto;
  }

  .text-body-2 {
    padding-left: 10px !important;
  }

  .auth-form-content {
    width: 100% !important;
    padding-top: 0px !important;
  }

  .auth-image-content {
    height: 206px !important;
    padding: 6px 0px 0px !important;
    order: 0 !important;
    width: 100% !important;
    &::after {
      display: none;
    }
    .img-content {
      img {
        width: 100% !important;
        height: 100% !important;
        object-fit: contain;
      }
    }
  }
  .row {
    &.form-row {
      padding-left: 0px !important;
      padding-right: 0px !important;
      margin-left: 0px !important;
      margin-right: 0px !important;
      margin: 0px !important;
    }
    .offset-1 {
      margin-left: 0px !important;
    }
    .form-link {
      padding-right: 0px !important;
      padding-left: 0px !important;
      a {
        margin-left: 0px !important;
      }
    }
  }
  .text-body-2, .text-h6 {
    margin-left: 15px !important;
  }
}

@media (max-width: 710px) {
  .auth-form-content {
    button[type="submit"] {
      min-width: 100% !important;
      max-width: 100% !important;
      margin-top: 30px;
    }
    .form-content {
      width: 100%;
    }
  }
  .temp-password-message {
    width: 100%;
  }
}
</style>
